import type { ColumnDef } from "@tanstack/react-table";
import * as React from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { api } from "$convex/convexApi";
import {
	AlertTriangle,
	CheckCircle2,
	ClipboardCheck,
	DollarSign,
	ExternalLink,
	FileText,
	Loader2,
	MapPin,
	Pencil,
	Receipt,
	Ship,
	StickyNote,
	Upload,
	Users,
	X,
} from "lucide-react";
import { toast } from "sonner";

import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableSkeleton } from "@/components/data-table/data-table-skeleton";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { InternalMetricCard } from "@/components/internal/shared/InternalMetricCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ComboboxSelect } from "@/components/ui/combobox-select";
import { DatePicker } from "@/components/ui/date-picker";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { formatCurrency } from "@/lib/format";
import { hasPermission } from "@/lib/roles";
import { cn } from "@/lib/utils";

const DOCUMENT_MAX_BYTES = 5 * 1024 * 1024;

function getTodaysDate(): string {
	const d = new Date();
	const yyyy = d.getFullYear();
	const mm = String(d.getMonth() + 1).padStart(2, "0");
	const dd = String(d.getDate()).padStart(2, "0");
	return `${yyyy}-${mm}-${dd}`;
}

const ANTHEM_ADDRESS = "13621 Wyandotte Street\nKansas City, MO 64145\nUnited States";

type SupplierRecord = {
	_id: string;
	name: string;
	address?: string;
	fdaRegistrationNumber?: string;
	paymentTerms?: string;
	paymentInstructions?: string;
	notes?: string;
};

const INTERNATIONAL_PURCHASE_STATUS_OPTIONS = [
	{ value: "collecting_documents", label: "Collecting Docs" },
	{ value: "ready_for_payment", label: "Ready for Payment" },
	{ value: "paid", label: "Paid" },
	{ value: "in_transit", label: "In Transit" },
	{ value: "received", label: "Received" },
	{ value: "cancelled", label: "Cancelled" },
] as const;

const PAYMENT_STATUS_OPTIONS = [
	{ value: "not_ready", label: "Not Ready" },
	{ value: "ready_to_pay", label: "Ready to Pay" },
	{ value: "paid", label: "Paid" },
] as const;

const DOCUMENT_TYPES = [
	{ key: "commercialInvoice", label: "Commercial Invoice", checklistKey: "commercialInvoiceReceived" },
	{ key: "isfForm", label: "ISF Form", checklistKey: "isfFormComplete" },
	{ key: "packingList", label: "Packing List", checklistKey: "packingListReceived" },
	{ key: "billOfLading", label: "Bill of Lading", checklistKey: "billOfLadingReceived" },
	{ key: "arrivalNotice", label: "Arrival Notice", checklistKey: "arrivalNoticeReceived" },
	{ key: "customsEntry", label: "Customs Entry / Broker Confirmation", checklistKey: "customsEntryReceived" },
	{ key: "freightInvoice", label: "Freight Invoice", checklistKey: "freightInvoiceReceived" },
	{ key: "paymentReceipt", label: "Payment Receipt", checklistKey: null },
] as const;

const IMPORT_CHECKLIST_ITEMS = DOCUMENT_TYPES.filter(
	(item): item is (typeof DOCUMENT_TYPES)[number] & { checklistKey: ChecklistKey } =>
		item.checklistKey !== null,
);

type InternationalPurchaseStatus = (typeof INTERNATIONAL_PURCHASE_STATUS_OPTIONS)[number]["value"];
type PaymentStatus = (typeof PAYMENT_STATUS_OPTIONS)[number]["value"];
type InternationalPurchaseDocumentType = (typeof DOCUMENT_TYPES)[number]["key"];
type ChecklistKey =
	| "commercialInvoiceReceived"
	| "isfFormComplete"
	| "packingListReceived"
	| "billOfLadingReceived"
	| "arrivalNoticeReceived"
	| "customsEntryReceived"
	| "freightInvoiceReceived";
type InternationalPurchasesTab = "all" | InternationalPurchaseStatus;
type InternationalPurchaseFormTab = "parties" | "invoice" | "routing" | "documents" | "notes";

const INTERNATIONAL_PURCHASE_FORM_TABS: {
	value: InternationalPurchaseFormTab;
	label: string;
	icon: typeof Users;
}[] = [
	{ value: "parties", label: "Parties", icon: Users },
	{ value: "invoice", label: "Invoice", icon: Receipt },
	{ value: "routing", label: "Routing", icon: MapPin },
	{ value: "documents", label: "Documents", icon: FileText },
	{ value: "notes", label: "Notes", icon: StickyNote },
];

type InternationalPurchaseDocumentMetadata = {
	url: string;
	r2Key: string;
	filename: string;
	originalFilename?: string;
	contentType: string;
	fileSize: number;
	uploadedAt: number;
};

type InternationalPurchaseRecord = {
	_id: string;
	id: string;
	supplierId?: string;
	supplierName: string;
	supplierAddress?: string;
	purchaseOrderNumber?: string;
	sellerReference?: string;
	invoiceNumber?: string;
	invoiceDate?: string;
	invoiceAmountUsd?: number;
	invoiceLineItems?: string;
	paymentTerms?: string;
	supplierPaymentInstructions?: string;
	paymentDueDate?: string;
	paymentStatus: PaymentStatus;
	paidAt?: number;
	masterBillOfLadingNumber?: string;
	houseBillOfLadingNumber?: string;
	quantityUnits?: string;
	containerCount?: number;
	vesselName?: string;
	portOfLoading?: string;
	destination?: string;
	estimatedSailingDate?: string;
	estimatedArrivalDate?: string;
	countryOfOrigin?: string;
	commodityDescription?: string;
	harmonizedTariffSchedule?: string;
	manufacturerName?: string;
	manufacturerAddress?: string;
	manufacturerFdaRegistrationNumber?: string;
	sellerName?: string;
	sellerAddress?: string;
	sellerFdaRegistrationNumber?: string;
	buyerName?: string;
	buyerAddress?: string;
	shipToName?: string;
	shipToAddress?: string;
	importerOfRecordName?: string;
	importerOfRecordAddress?: string;
	importerOfRecordFederalId?: string;
	consigneeName?: string;
	consigneeAddress?: string;
	consigneeFederalId?: string;
	containerStuffingLocationName?: string;
	containerStuffingLocationAddress?: string;
	containerStuffingLocationFdaRegistrationNumber?: string;
	containerStufferName?: string;
	containerStufferAddress?: string;
	containerStufferFdaRegistrationNumber?: string;
	commercialInvoiceReceived: boolean;
	isfFormComplete: boolean;
	packingListReceived: boolean;
	billOfLadingReceived: boolean;
	arrivalNoticeReceived: boolean;
	customsEntryReceived: boolean;
	freightInvoiceReceived: boolean;
	commercialInvoiceDocument?: InternationalPurchaseDocumentMetadata;
	isfFormDocument?: InternationalPurchaseDocumentMetadata;
	packingListDocument?: InternationalPurchaseDocumentMetadata;
	billOfLadingDocument?: InternationalPurchaseDocumentMetadata;
	arrivalNoticeDocument?: InternationalPurchaseDocumentMetadata;
	customsEntryDocument?: InternationalPurchaseDocumentMetadata;
	freightInvoiceDocument?: InternationalPurchaseDocumentMetadata;
	paymentReceiptDocument?: InternationalPurchaseDocumentMetadata;
	notes?: string;
	status: InternationalPurchaseStatus;
	createdAt: number;
	updatedAt: number;
	createdByLabel?: string;
	updatedByLabel?: string;
};

type InternationalPurchaseFormState = {
	supplierId: string;
	supplierName: string;
	supplierAddress: string;
	purchaseOrderNumber: string;
	sellerReference: string;
	invoiceNumber: string;
	invoiceDate: string;
	invoiceAmountUsd: string;
	invoiceLineItems: string;
	paymentTerms: string;
	supplierPaymentInstructions: string;
	paymentDueDate: string;
	paymentStatus: PaymentStatus;
	masterBillOfLadingNumber: string;
	houseBillOfLadingNumber: string;
	quantityUnits: string;
	containerCount: string;
	vesselName: string;
	portOfLoading: string;
	destination: string;
	estimatedSailingDate: string;
	estimatedArrivalDate: string;
	countryOfOrigin: string;
	commodityDescription: string;
	harmonizedTariffSchedule: string;
	manufacturerName: string;
	manufacturerAddress: string;
	manufacturerFdaRegistrationNumber: string;
	sellerName: string;
	sellerAddress: string;
	sellerFdaRegistrationNumber: string;
	buyerName: string;
	buyerAddress: string;
	shipToName: string;
	shipToAddress: string;
	importerOfRecordName: string;
	importerOfRecordAddress: string;
	importerOfRecordFederalId: string;
	consigneeName: string;
	consigneeAddress: string;
	consigneeFederalId: string;
	containerStuffingLocationName: string;
	containerStuffingLocationAddress: string;
	containerStuffingLocationFdaRegistrationNumber: string;
	containerStufferName: string;
	containerStufferAddress: string;
	containerStufferFdaRegistrationNumber: string;
	commercialInvoiceReceived: boolean;
	isfFormComplete: boolean;
	packingListReceived: boolean;
	billOfLadingReceived: boolean;
	arrivalNoticeReceived: boolean;
	customsEntryReceived: boolean;
	freightInvoiceReceived: boolean;
	notes: string;
	status: InternationalPurchaseStatus;
};

type InternationalPurchaseDocumentDraft = {
	file: File | null;
	removeExisting: boolean;
};

type InternationalPurchaseDocumentDrafts = Record<
	InternationalPurchaseDocumentType,
	InternationalPurchaseDocumentDraft
>;

type InternationalPurchaseDialogState =
	| { mode: "create" }
	| { mode: "edit"; purchase: InternationalPurchaseRecord };

type TextFormFieldName = Extract<
	keyof InternationalPurchaseFormState,
	| "supplierAddress"
	| "invoiceLineItems"
	| "supplierPaymentInstructions"
	| "manufacturerName"
	| "manufacturerAddress"
	| "manufacturerFdaRegistrationNumber"
	| "sellerName"
	| "sellerAddress"
	| "sellerFdaRegistrationNumber"
	| "buyerName"
	| "buyerAddress"
	| "shipToName"
	| "shipToAddress"
	| "importerOfRecordName"
	| "importerOfRecordAddress"
	| "importerOfRecordFederalId"
	| "consigneeName"
	| "consigneeAddress"
	| "consigneeFederalId"
	| "containerStuffingLocationName"
	| "containerStuffingLocationAddress"
	| "containerStuffingLocationFdaRegistrationNumber"
	| "containerStufferName"
	| "containerStufferAddress"
	| "containerStufferFdaRegistrationNumber"
>;

type IsfPartySection = {
	requirementNumber: number;
	title: string;
	source: "seller" | "importer";
	nameField: TextFormFieldName;
	namePlaceholder: string;
	addressField: TextFormFieldName;
	addressPlaceholder: string;
	idField?: TextFormFieldName;
	idLabel?: string;
	idPlaceholder?: string;
};

const ISF_PARTY_SECTIONS: IsfPartySection[] = [
	{
		requirementNumber: 1,
		title: "Supplier / Manufacturer",
		source: "seller",
		nameField: "manufacturerName",
		namePlaceholder: "Supplier or manufacturer name",
		addressField: "manufacturerAddress",
		addressPlaceholder: "Street, city, province, postal code, country",
		idField: "manufacturerFdaRegistrationNumber",
		idLabel: "FDA Registration #",
		idPlaceholder: "FDA registration number",
	},
	{
		requirementNumber: 2,
		title: "Seller",
		source: "seller",
		nameField: "sellerName",
		namePlaceholder: "Seller name",
		addressField: "sellerAddress",
		addressPlaceholder: "Street, city, province, postal code, country",
		idField: "sellerFdaRegistrationNumber",
		idLabel: "FDA Registration #",
		idPlaceholder: "FDA registration number",
	},
	{
		requirementNumber: 3,
		title: "Buyer",
		source: "importer",
		nameField: "buyerName",
		namePlaceholder: "Buyer name",
		addressField: "buyerAddress",
		addressPlaceholder: "Street, city, province, postal code, country",
	},
	{
		requirementNumber: 4,
		title: "Ship To",
		source: "importer",
		nameField: "shipToName",
		namePlaceholder: "Ship-to name",
		addressField: "shipToAddress",
		addressPlaceholder: "Street, city, province, postal code, country",
	},
	{
		requirementNumber: 5,
		title: "Container Stuffing Location",
		source: "seller",
		nameField: "containerStuffingLocationName",
		namePlaceholder: "Stuffing location name",
		addressField: "containerStuffingLocationAddress",
		addressPlaceholder: "Street, city, province, postal code, country",
		idField: "containerStuffingLocationFdaRegistrationNumber",
		idLabel: "FDA Registration #",
		idPlaceholder: "FDA registration number",
	},
	{
		requirementNumber: 6,
		title: "Container Stuffer",
		source: "seller",
		nameField: "containerStufferName",
		namePlaceholder: "Container stuffer name",
		addressField: "containerStufferAddress",
		addressPlaceholder: "Street, city, province, postal code, country",
		idField: "containerStufferFdaRegistrationNumber",
		idLabel: "FDA Registration #",
		idPlaceholder: "FDA registration number",
	},
	{
		requirementNumber: 7,
		title: "Importer of Record",
		source: "importer",
		nameField: "importerOfRecordName",
		namePlaceholder: "Importer of record",
		addressField: "importerOfRecordAddress",
		addressPlaceholder: "Street, city, province, postal code, country",
		idField: "importerOfRecordFederalId",
		idLabel: "Federal ID",
		idPlaceholder: "Federal ID number",
	},
	{
		requirementNumber: 8,
		title: "Consignee",
		source: "importer",
		nameField: "consigneeName",
		namePlaceholder: "Consignee name",
		addressField: "consigneeAddress",
		addressPlaceholder: "Street, city, province, postal code, country",
		idField: "consigneeFederalId",
		idLabel: "Federal ID",
		idPlaceholder: "Federal ID number",
	},
];

const ANTHEM_PARTY_NAME = "Anthem Coffee Imports";
const ANTHEM_PARTY_ADDRESS =
	"13621 Wyandotte Street\nKansas City, MO 64145\nUnited States";

function withAnthemImporterPartyDefaults(
	form: InternationalPurchaseFormState,
): InternationalPurchaseFormState {
	return {
		...form,
		buyerName: ANTHEM_PARTY_NAME,
		buyerAddress: ANTHEM_PARTY_ADDRESS,
		shipToName: ANTHEM_PARTY_NAME,
		shipToAddress: ANTHEM_PARTY_ADDRESS,
		importerOfRecordName: ANTHEM_PARTY_NAME,
		importerOfRecordAddress: ANTHEM_PARTY_ADDRESS,
		consigneeName: ANTHEM_PARTY_NAME,
		consigneeAddress: ANTHEM_PARTY_ADDRESS,
	};
}

function buildCreateFormState(): InternationalPurchaseFormState {
	return withAnthemImporterPartyDefaults(buildEmptyFormState());
}

function buildEmptyFormState(): InternationalPurchaseFormState {
	return {
		supplierId: "",
		supplierName: "",
		supplierAddress: "",
		purchaseOrderNumber: "",
		sellerReference: "",
		invoiceNumber: "",
		invoiceDate: getTodaysDate(),
		invoiceAmountUsd: "",
		invoiceLineItems: "",
		paymentTerms: "",
		supplierPaymentInstructions: "",
		paymentDueDate: getTodaysDate(),
		paymentStatus: "not_ready",
		masterBillOfLadingNumber: "",
		houseBillOfLadingNumber: "",
		quantityUnits: "",
		containerCount: "",
		vesselName: "",
		portOfLoading: "",
		destination: "",
		estimatedSailingDate: "",
		estimatedArrivalDate: "",
		countryOfOrigin: "",
		commodityDescription: "",
		harmonizedTariffSchedule: "",
		manufacturerName: "",
		manufacturerAddress: "",
		manufacturerFdaRegistrationNumber: "",
		sellerName: "",
		sellerAddress: "",
		sellerFdaRegistrationNumber: "",
		buyerName: "Anthem Coffee Imports",
		buyerAddress: ANTHEM_ADDRESS,
		shipToName: "Anthem Coffee Imports",
		shipToAddress: ANTHEM_ADDRESS,
		importerOfRecordName: "Anthem Coffee Imports",
		importerOfRecordAddress: ANTHEM_ADDRESS,
		importerOfRecordFederalId: "",
		consigneeName: "Anthem Coffee Imports",
		consigneeAddress: ANTHEM_ADDRESS,
		consigneeFederalId: "",
		containerStuffingLocationName: "",
		containerStuffingLocationAddress: "",
		containerStuffingLocationFdaRegistrationNumber: "",
		containerStufferName: "",
		containerStufferAddress: "",
		containerStufferFdaRegistrationNumber: "",
		commercialInvoiceReceived: false,
		isfFormComplete: false,
		packingListReceived: false,
		billOfLadingReceived: false,
		arrivalNoticeReceived: false,
		customsEntryReceived: false,
		freightInvoiceReceived: false,
		notes: "",
		status: "collecting_documents",
	};
}

function buildFormState(purchase?: InternationalPurchaseRecord): InternationalPurchaseFormState {
	if (!purchase) return buildEmptyFormState();
	return {
		supplierId: purchase.supplierId ?? "",
		supplierName: purchase.supplierName,
		supplierAddress: purchase.supplierAddress ?? "",
		purchaseOrderNumber: purchase.purchaseOrderNumber ?? "",
		sellerReference: purchase.sellerReference ?? "",
		invoiceNumber: purchase.invoiceNumber ?? "",
		invoiceDate: purchase.invoiceDate ?? "",
		invoiceAmountUsd:
			typeof purchase.invoiceAmountUsd === "number" ? String(purchase.invoiceAmountUsd) : "",
		invoiceLineItems: purchase.invoiceLineItems ?? "",
		paymentTerms: purchase.paymentTerms ?? "",
		supplierPaymentInstructions: purchase.supplierPaymentInstructions ?? "",
		paymentDueDate: purchase.paymentDueDate ?? "",
		paymentStatus: purchase.paymentStatus,
		masterBillOfLadingNumber: purchase.masterBillOfLadingNumber ?? "",
		houseBillOfLadingNumber: purchase.houseBillOfLadingNumber ?? "",
		quantityUnits: purchase.quantityUnits ?? "",
		containerCount:
			typeof purchase.containerCount === "number" ? String(purchase.containerCount) : "",
		vesselName: purchase.vesselName ?? "",
		portOfLoading: purchase.portOfLoading ?? "",
		destination: purchase.destination ?? "",
		estimatedSailingDate: purchase.estimatedSailingDate ?? "",
		estimatedArrivalDate: purchase.estimatedArrivalDate ?? "",
		countryOfOrigin: purchase.countryOfOrigin ?? "",
		commodityDescription: purchase.commodityDescription ?? "",
		harmonizedTariffSchedule: purchase.harmonizedTariffSchedule ?? "",
		manufacturerName: purchase.manufacturerName ?? "",
		manufacturerAddress: purchase.manufacturerAddress ?? "",
		manufacturerFdaRegistrationNumber: purchase.manufacturerFdaRegistrationNumber ?? "",
		sellerName: purchase.sellerName ?? "",
		sellerAddress: purchase.sellerAddress ?? "",
		sellerFdaRegistrationNumber: purchase.sellerFdaRegistrationNumber ?? "",
		buyerName: purchase.buyerName ?? "",
		buyerAddress: purchase.buyerAddress ?? "",
		shipToName: purchase.shipToName ?? "",
		shipToAddress: purchase.shipToAddress ?? "",
		importerOfRecordName: purchase.importerOfRecordName ?? "",
		importerOfRecordAddress: purchase.importerOfRecordAddress ?? "",
		importerOfRecordFederalId: purchase.importerOfRecordFederalId ?? "",
		consigneeName: purchase.consigneeName ?? "",
		consigneeAddress: purchase.consigneeAddress ?? "",
		consigneeFederalId: purchase.consigneeFederalId ?? "",
		containerStuffingLocationName: purchase.containerStuffingLocationName ?? "",
		containerStuffingLocationAddress: purchase.containerStuffingLocationAddress ?? "",
		containerStuffingLocationFdaRegistrationNumber: purchase.containerStuffingLocationFdaRegistrationNumber ?? "",
		containerStufferName: purchase.containerStufferName ?? "",
		containerStufferAddress: purchase.containerStufferAddress ?? "",
		containerStufferFdaRegistrationNumber: purchase.containerStufferFdaRegistrationNumber ?? "",
		commercialInvoiceReceived: purchase.commercialInvoiceReceived,
		isfFormComplete: purchase.isfFormComplete,
		packingListReceived: purchase.packingListReceived,
		billOfLadingReceived: purchase.billOfLadingReceived,
		arrivalNoticeReceived: purchase.arrivalNoticeReceived,
		customsEntryReceived: purchase.customsEntryReceived,
		freightInvoiceReceived: purchase.freightInvoiceReceived,
		notes: purchase.notes ?? "",
		status: purchase.status,
	};
}

function createEmptyDocumentDrafts(): InternationalPurchaseDocumentDrafts {
	return DOCUMENT_TYPES.reduce((drafts, documentType) => {
		drafts[documentType.key] = { file: null, removeExisting: false };
		return drafts;
	}, {} as InternationalPurchaseDocumentDrafts);
}

function getDocumentLabel(documentType: InternationalPurchaseDocumentType): string {
	return DOCUMENT_TYPES.find((item) => item.key === documentType)?.label ?? documentType;
}

function getDocumentFieldName(documentType: InternationalPurchaseDocumentType) {
	if (documentType === "commercialInvoice") return "commercialInvoiceDocument";
	if (documentType === "isfForm") return "isfFormDocument";
	if (documentType === "packingList") return "packingListDocument";
	if (documentType === "billOfLading") return "billOfLadingDocument";
	if (documentType === "arrivalNotice") return "arrivalNoticeDocument";
	if (documentType === "customsEntry") return "customsEntryDocument";
	if (documentType === "freightInvoice") return "freightInvoiceDocument";
	return "paymentReceiptDocument";
}

function getPurchaseDocument(
	purchase: InternationalPurchaseRecord | undefined,
	documentType: InternationalPurchaseDocumentType,
) {
	if (!purchase) return undefined;
	return purchase[getDocumentFieldName(documentType)] as
		| InternationalPurchaseDocumentMetadata
		| undefined;
}

function formatDateOnly(value: string | undefined): string {
	if (!value) return "-";
	const [year = Number.NaN, month = Number.NaN, day = Number.NaN] = value
		.split("-")
		.map(Number);
	const date = new Date(Date.UTC(year, month - 1, day));
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat("en-US", {
		timeZone: "UTC",
		month: "short",
		day: "numeric",
		year: "numeric",
	}).format(date);
}

function formatTimestamp(timestamp: number | undefined): string {
	if (typeof timestamp !== "number" || !Number.isFinite(timestamp)) return "-";
	return new Intl.DateTimeFormat("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
	}).format(new Date(timestamp));
}

function formatFileSize(fileSize: number | undefined): string {
	if (typeof fileSize !== "number" || !Number.isFinite(fileSize) || fileSize <= 0) {
		return "";
	}
	if (fileSize >= 1024 * 1024) {
		return `${(fileSize / (1024 * 1024)).toFixed(1)} MB`;
	}
	return `${Math.max(1, Math.round(fileSize / 1024))} KB`;
}

function getFileExtension(filename: string | undefined): string {
	const normalized = String(filename ?? "").trim().toLowerCase();
	const lastDotIndex = normalized.lastIndexOf(".");
	if (lastDotIndex === -1 || lastDotIndex === normalized.length - 1) return "";
	return normalized.slice(lastDotIndex + 1);
}

function isAllowedDocumentFile(file: File): boolean {
	const extension = getFileExtension(file.name);
	return (
		["pdf", "xlsx", "xls", "csv"].includes(extension) ||
		[
			"application/pdf",
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			"application/vnd.ms-excel",
			"text/csv",
			"application/csv",
		].includes(file.type)
	);
}

function getStatusLabel(status: InternationalPurchaseStatus): string {
	return (
		INTERNATIONAL_PURCHASE_STATUS_OPTIONS.find((option) => option.value === status)?.label ??
		status
	);
}

function getPaymentStatusLabel(status: PaymentStatus): string {
	return PAYMENT_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;
}

function getStatusBadgeClass(status: InternationalPurchaseStatus): string {
	if (status === "received") {
		return "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200";
	}
	if (status === "paid" || status === "in_transit") {
		return "border-blue-300 bg-blue-100 text-blue-900 dark:border-blue-800/60 dark:bg-blue-950/40 dark:text-blue-200";
	}
	if (status === "ready_for_payment") {
		return "border-violet-300 bg-violet-100 text-violet-900 dark:border-violet-800/60 dark:bg-violet-950/40 dark:text-violet-200";
	}
	if (status === "cancelled") {
		return "border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-700/60 dark:bg-slate-900/40 dark:text-slate-200";
	}
	return "border-yellow-300 bg-yellow-100 text-yellow-900 dark:border-yellow-800/60 dark:bg-yellow-950/40 dark:text-yellow-200";
}

function getPaymentStatusBadgeClass(status: PaymentStatus): string {
	if (status === "paid") {
		return "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200";
	}
	if (status === "ready_to_pay") {
		return "border-violet-300 bg-violet-100 text-violet-900 dark:border-violet-800/60 dark:bg-violet-950/40 dark:text-violet-200";
	}
	return "border-yellow-300 bg-yellow-100 text-yellow-900 dark:border-yellow-800/60 dark:bg-yellow-950/40 dark:text-yellow-200";
}

function getChecklistCompletion<T extends Record<ChecklistKey, boolean>>(purchase: T) {
	const completed = IMPORT_CHECKLIST_ITEMS.filter(
		(item) => purchase[item.checklistKey],
	).length;
	return { completed, total: IMPORT_CHECKLIST_ITEMS.length };
}

function getMissingChecklistLabels<T extends Record<ChecklistKey, boolean>>(purchase: T) {
	return IMPORT_CHECKLIST_ITEMS.filter((item) => !purchase[item.checklistKey]).map(
		(item) => item.label,
	);
}

function getNextAction(
	purchase: Record<ChecklistKey, boolean> & {
		status: InternationalPurchaseStatus;
		paymentStatus: PaymentStatus;
	},
) {
	const missingDocs = getMissingChecklistLabels(purchase);
	if (purchase.status === "cancelled") {
		return { label: "Closed", detail: "Purchase is cancelled.", tone: "muted" as const };
	}
	if (purchase.status === "received") {
		return { label: "Received", detail: "Shipment has been received.", tone: "success" as const };
	}
	if (missingDocs.length > 0) {
		const missingSummary = missingDocs.slice(0, 2).join(", ");
		return {
			label: "Collect docs",
			detail: `Missing ${missingSummary}${missingDocs.length > 2 ? ` +${missingDocs.length - 2} more` : ""}`,
			tone: "warning" as const,
		};
	}
	if (purchase.paymentStatus === "not_ready") {
		return {
			label: "Review payment",
			detail: "Docs complete; mark ready when invoice is approved.",
			tone: "warning" as const,
		};
	}
	if (purchase.paymentStatus === "ready_to_pay") {
		return {
			label: "Pay invoice",
			detail: "Docs complete and payment is marked ready.",
			tone: "ready" as const,
		};
	}
	if (purchase.paymentStatus === "paid" && purchase.status !== "in_transit") {
		return {
			label: "Track shipment",
			detail: "Payment recorded; move to in transit when B/L release is confirmed.",
			tone: "info" as const,
		};
	}
	return { label: "Monitor", detail: "No immediate blocker flagged.", tone: "info" as const };
}

function getNextActionBadgeClass(tone: ReturnType<typeof getNextAction>["tone"]): string {
	if (tone === "success") return "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200";
	if (tone === "ready") return "border-violet-300 bg-violet-100 text-violet-900 dark:border-violet-800/60 dark:bg-violet-950/40 dark:text-violet-200";
	if (tone === "info") return "border-blue-300 bg-blue-100 text-blue-900 dark:border-blue-800/60 dark:bg-blue-950/40 dark:text-blue-200";
	if (tone === "muted") return "border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-700/60 dark:bg-slate-900/40 dark:text-slate-200";
	return "border-yellow-300 bg-yellow-100 text-yellow-900 dark:border-yellow-800/60 dark:bg-yellow-950/40 dark:text-yellow-200";
}

function buildSupplierComboboxOptions(
	suppliers: readonly SupplierRecord[] | undefined,
): Array<{ value: string; label: string; sublabel?: string }> {
	if (!suppliers) return [];
	return suppliers.map((s) => ({
		value: s._id,
		label: s.name,
		sublabel: s.address?.split("\n")[0] || s.fdaRegistrationNumber || undefined,
	}));
}

function getDocumentLinks(purchase: InternationalPurchaseRecord) {
	return DOCUMENT_TYPES.flatMap((documentType) => {
		const document = getPurchaseDocument(purchase, documentType.key);
		if (!document?.url) return [];
		return [
			{
				key: documentType.key,
				label: documentType.label,
				url: document.url,
				filename: document.originalFilename ?? document.filename,
				fileSize: document.fileSize,
				sourceLabel: getFileExtension(document.originalFilename ?? document.filename).toUpperCase(),
			},
		];
	});
}

function nullableText(value: string): string | null {
	const normalized = value.trim();
	return normalized ? normalized : null;
}

function nullableAmount(value: string): number | null {
	const normalized = value.trim();
	if (!normalized) return null;
	const numeric = Number(normalized.replace(/,/g, ""));
	if (!Number.isFinite(numeric) || numeric < 0) {
		throw new Error("Invoice amount must be a non-negative number.");
	}
	return numeric;
}

function nullableContainerCount(value: string): number | null {
	const normalized = value.trim();
	if (!normalized) return null;
	const numeric = Number(normalized);
	if (!Number.isInteger(numeric) || numeric < 0) {
		throw new Error("Number of containers must be a non-negative whole number.");
	}
	return numeric;
}

function ChecklistProgressCell({ purchase }: { purchase: InternationalPurchaseRecord }) {
	const completion = getChecklistCompletion(purchase);
	const isComplete = completion.completed === completion.total;
	return (
		<div className="flex items-center gap-2">
			<Badge
				variant="outline"
				className={
					isComplete
						? "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200"
						: "border-yellow-300 bg-yellow-100 text-yellow-900 dark:border-yellow-800/60 dark:bg-yellow-950/40 dark:text-yellow-200"
				}
			>
				{completion.completed}/{completion.total}
			</Badge>
			<span className="text-xs text-muted-foreground">
				{isComplete ? "Docs ready" : "Waiting"}
			</span>
		</div>
	);
}

function FormSection({
	title,
	description,
	icon: Icon,
	actions,
	children,
	className,
}: {
	title: string;
	description?: string;
	icon?: typeof Receipt;
	actions?: React.ReactNode;
	children: React.ReactNode;
	className?: string;
}) {
	return (
		<section
			className={cn(
				"rounded-xl bg-muted/30 p-4 shadow-sm ring-1 ring-border/50",
				className,
			)}
		>
			<div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
				<div className="flex min-w-0 items-start gap-2.5">
					{Icon ? <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" /> : null}
					<div className="min-w-0">
						<h3 className="text-sm font-semibold text-balance text-foreground">{title}</h3>
						{description ? (
							<p className="mt-0.5 text-xs text-pretty text-muted-foreground">{description}</p>
						) : null}
					</div>
				</div>
				{actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
			</div>
			{children}
		</section>
	);
}

function FormSubsection({
	title,
	description,
	children,
}: {
	title: string;
	description?: string;
	children: React.ReactNode;
}) {
	return (
		<div className="space-y-4">
			<div>
				<p className="text-sm font-medium text-foreground">{title}</p>
				{description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
			</div>
			{children}
		</div>
	);
}

function CompactReadinessBar({ purchase }: { purchase: InternationalPurchaseFormState }) {
	const completion = getChecklistCompletion(purchase);
	const nextAction = getNextAction(purchase);
	const completionPercent = completion.total > 0 ? (completion.completed / completion.total) * 100 : 0;

	return (
		<div className="rounded-xl bg-muted/40 px-4 py-3 ring-1 ring-border/50">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="flex min-w-0 flex-wrap items-center gap-2">
					<Badge variant="outline" className={getNextActionBadgeClass(nextAction.tone)}>
						{nextAction.label}
					</Badge>
					<span className="text-xs text-muted-foreground">{nextAction.detail}</span>
				</div>
				<div className="flex items-center gap-3 sm:w-[280px]">
					<div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-muted">
						<div
							className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
							style={{ width: `${completionPercent}%` }}
						/>
					</div>
					<span className="shrink-0 text-xs font-medium tabular-nums text-muted-foreground">
						{completion.completed}/{completion.total} docs
					</span>
				</div>
			</div>
		</div>
	);
}

function getIsfPartyStatus(
	section: IsfPartySection,
	form: InternationalPurchaseFormState,
): "empty" | "partial" | "complete" {
	const name = form[section.nameField].trim();
	const address = form[section.addressField].trim();
	if (!name && !address) return "empty";
	if (name && address) return "complete";
	return "partial";
}

function getIsfPartyStatusLabel(status: ReturnType<typeof getIsfPartyStatus>): string {
	if (status === "complete") return "Complete";
	if (status === "partial") return "In progress";
	return "Not started";
}

function getIsfPartyStatusBadgeClass(status: ReturnType<typeof getIsfPartyStatus>): string {
	if (status === "complete") {
		return "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200";
	}
	if (status === "partial") {
		return "border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200";
	}
	return "border-border bg-muted/60 text-muted-foreground";
}

function IsfPartyCard({
	section,
	form,
	onSetTextField,
	headerExtra,
}: {
	section: IsfPartySection;
	form: InternationalPurchaseFormState;
	onSetTextField: (field: TextFormFieldName, value: string) => void;
	headerExtra?: React.ReactNode;
}) {
	const status = getIsfPartyStatus(section, form);
	const idField = section.idField;

	return (
		<div className="overflow-hidden rounded-xl bg-background ring-1 ring-border/60">
			<div className="flex items-center gap-3 border-b border-border/60 px-4 py-3">
				<Badge variant="secondary" className="shrink-0 rounded-full tabular-nums">
					#{section.requirementNumber}
				</Badge>
				<div className="min-w-0 flex-1">
					<p className="text-sm font-semibold text-foreground">{section.title}</p>
					<p className="text-xs text-muted-foreground">
						{section.source === "seller" ? "Seller provides" : "Importer provides"}
					</p>
				</div>
				<Badge variant="outline" className={cn("shrink-0", getIsfPartyStatusBadgeClass(status))}>
					{status === "complete" ? (
						<>
							<CheckCircle2 className="mr-1 size-3" />
							Done
						</>
					) : (
						getIsfPartyStatusLabel(status)
					)}
				</Badge>
			</div>

			<div className="space-y-3 px-4 py-4">
				{headerExtra}
				<div className="grid gap-3">
					<div className="grid gap-2">
						<Label htmlFor={`international-purchase-${section.nameField}`}>Name</Label>
						<Input
							id={`international-purchase-${section.nameField}`}
							className="shadow-none"
							value={form[section.nameField]}
							onChange={(event) => onSetTextField(section.nameField, event.target.value)}
							placeholder={section.namePlaceholder}
						/>
					</div>

					{idField ? (
						<div className="grid max-w-md gap-2">
							<Label htmlFor={`international-purchase-${idField}`}>{section.idLabel}</Label>
							<Input
								id={`international-purchase-${idField}`}
								className="shadow-none"
								value={form[idField]}
								onChange={(event) => onSetTextField(idField, event.target.value)}
								placeholder={section.idPlaceholder}
							/>
						</div>
					) : null}

					<div className="grid gap-2">
						<Label htmlFor={`international-purchase-${section.addressField}`}>Address</Label>
						<Textarea
							id={`international-purchase-${section.addressField}`}
							value={form[section.addressField]}
							onChange={(event) => onSetTextField(section.addressField, event.target.value)}
							placeholder={section.addressPlaceholder}
							rows={3}
							className="min-h-[84px] resize-y shadow-none"
						/>
					</div>
				</div>
			</div>
		</div>
	);
}

function IsfPartiesPanel({
	form,
	suppliers,
	setForm,
	onSupplierSelect,
}: {
	form: InternationalPurchaseFormState;
	suppliers: readonly SupplierRecord[] | undefined;
	setForm: React.Dispatch<React.SetStateAction<InternationalPurchaseFormState>>;
	onSupplierSelect: (supplierId: string) => void;
}) {
	const handlePartyTextField = React.useCallback(
		(section: IsfPartySection, field: TextFormFieldName, value: string) => {
			setForm((current) => {
				const next = { ...current, [field]: value };
				if (section.requirementNumber === 1) {
					if (field === "manufacturerName") {
						next.supplierName = value;
						next.supplierId = "";
					}
					if (field === "manufacturerAddress") {
						next.supplierAddress = value;
					}
				}
				return next;
			});
		},
		[setForm],
	);

	const supplierCombobox = (
		<div className="grid gap-2 pb-1">
			<Label htmlFor="international-purchase-supplier-combobox">
				Saved supplier <span className="font-normal text-muted-foreground">(optional)</span>
			</Label>
			<ComboboxSelect
				options={buildSupplierComboboxOptions(suppliers)}
				value={form.supplierId}
				loading={suppliers === undefined}
				placeholder="Search suppliers…"
				searchPlaceholder="Search suppliers…"
				emptyText="No suppliers found. Enter details below."
				disablePortal
				onValueChange={onSupplierSelect}
			/>
			<p className="text-xs text-muted-foreground">
				Auto-fills seller parties #1, #2, and payment defaults.
			</p>
		</div>
	);

	return (
		<div className="space-y-5">
			{(["seller", "importer"] as const).map((source) => {
				const sections = ISF_PARTY_SECTIONS.filter((section) => section.source === source);
				return (
					<section key={source} className="space-y-2">
						<div className="space-y-2">
							{sections.map((section) => (
								<IsfPartyCard
									key={section.requirementNumber}
									section={section}
									form={form}
									onSetTextField={(field, value) => handlePartyTextField(section, field, value)}
									headerExtra={section.requirementNumber === 1 ? supplierCombobox : undefined}
								/>
							))}
						</div>
					</section>
				);
			})}
		</div>
	);
}

function InternationalPurchaseDocumentsCell({ purchase }: { purchase: InternationalPurchaseRecord }) {
	const [open, setOpen] = React.useState(false);
	const documents = React.useMemo(() => getDocumentLinks(purchase), [purchase]);

	if (documents.length === 0) {
		return <span className="text-xs text-muted-foreground">—</span>;
	}

	return (
		<div className="flex items-center gap-1.5">
			<Button
				type="button"
				variant="outline"
				size="xs"
				className="h-7 gap-1.5"
				onClick={() => setOpen(true)}
			>
				<FileText className="size-3.5" />
				Docs ({documents.length})
			</Button>
			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="sm:max-w-[640px]">
					<DialogHeader>
						<DialogTitle>International Purchase Documents</DialogTitle>
						<DialogDescription>
							{purchase.supplierName}
							{purchase.invoiceNumber ? ` · Invoice ${purchase.invoiceNumber}` : ""}
						</DialogDescription>
					</DialogHeader>
					<div className="grid gap-3 py-2">
						{documents.map((document) => (
							<div
								key={document.key}
								className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between"
							>
								<div className="min-w-0">
									<div className="flex items-center gap-2">
										<FileText className="size-4 text-muted-foreground" />
										<p className="font-medium text-foreground">{document.label}</p>
									</div>
									<p className="truncate text-sm text-muted-foreground">
										{document.filename}
										{formatFileSize(document.fileSize) ? ` · ${formatFileSize(document.fileSize)}` : ""}
									</p>
								</div>
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={() => window.open(document.url, "_blank", "noopener,noreferrer")}
								>
									<ExternalLink className="mr-1.5 size-4" />
									Open {document.sourceLabel || "File"}
								</Button>
							</div>
						))}
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}

function InternationalPurchaseDocumentUploadField({
	documentType,
	existingDocument,
	draft,
	onSelectFile,
	onClearSelectedFile,
	onMarkRemoveExisting,
	onUndoRemoveExisting,
}: {
	documentType: InternationalPurchaseDocumentType;
	existingDocument?: InternationalPurchaseDocumentMetadata;
	draft: InternationalPurchaseDocumentDraft;
	onSelectFile: (file: File) => void;
	onClearSelectedFile: () => void;
	onMarkRemoveExisting: () => void;
	onUndoRemoveExisting: () => void;
}) {
	const inputRef = React.useRef<HTMLInputElement | null>(null);
	const label = getDocumentLabel(documentType);
	const selectedFile = draft.file;
	const isRemovingExisting = draft.removeExisting;
	const currentFilename = selectedFile?.name ?? existingDocument?.originalFilename ?? existingDocument?.filename;
	const fileSizeLabel = selectedFile
		? formatFileSize(selectedFile.size)
		: formatFileSize(existingDocument?.fileSize);
	const statusLabel = selectedFile
		? existingDocument && !isRemovingExisting
			? "Will replace current file when you save."
			: "Will upload when you save."
		: isRemovingExisting
			? "Will be removed when you save."
			: existingDocument
				? "Already uploaded to this purchase."
				: "Optional until available. Accepts PDF, XLSX, XLS, or CSV.";

	return (
		<div
			className={`rounded-xl p-4 transition-shadow ${
				selectedFile || existingDocument
					? "bg-background shadow-sm ring-1 ring-border/70"
					: "border border-dashed border-border/80 bg-muted/20 hover:border-border hover:bg-muted/30"
			}`}
		>
			<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
				<div className="min-w-0 space-y-1">
					<div className="flex items-center gap-2">
						<FileText className="size-4 shrink-0 text-muted-foreground" />
						<p className="text-sm font-medium text-foreground">{label}</p>
					</div>
					<p className="text-xs text-muted-foreground">{statusLabel}</p>
					{currentFilename ? (
						<p className="truncate text-sm text-foreground">
							{currentFilename}
							{fileSizeLabel ? <span className="text-muted-foreground"> · {fileSizeLabel}</span> : null}
						</p>
					) : null}
				</div>

				<div className="flex flex-wrap gap-2">
					<Button
						type="button"
						variant={existingDocument && !selectedFile && !isRemovingExisting ? "outline" : "default"}
						size="sm"
						className="transition-transform active:scale-[0.96]"
						onClick={() => inputRef.current?.click()}
					>
						<Upload className="mr-2 size-4" />
						{existingDocument && !isRemovingExisting ? "Replace" : "Choose File"}
					</Button>

					{selectedFile ? (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="transition-transform active:scale-[0.96]"
							onClick={onClearSelectedFile}
						>
							<X className="mr-2 size-4" />
							Clear
						</Button>
					) : null}

					{existingDocument && !selectedFile && !isRemovingExisting ? (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="transition-transform active:scale-[0.96]"
							onClick={onMarkRemoveExisting}
						>
							Remove
						</Button>
					) : null}

					{isRemovingExisting ? (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="transition-transform active:scale-[0.96]"
							onClick={onUndoRemoveExisting}
						>
							Undo Remove
						</Button>
					) : null}
				</div>
			</div>

			<input
				ref={inputRef}
				type="file"
				accept="application/pdf,.pdf,.xlsx,.xls,.csv,text/csv"
				className="hidden"
				onChange={(event) => {
					const file = event.target.files?.[0];
					if (file) onSelectFile(file);
					event.currentTarget.value = "";
				}}
			/>
		</div>
	);
}

function useColumns(onEdit: (purchase: InternationalPurchaseRecord) => void, canEdit: boolean) {
	return React.useMemo<ColumnDef<InternationalPurchaseRecord>[]>(() => {
		const columns: ColumnDef<InternationalPurchaseRecord>[] = [
			{
				id: "status",
				accessorKey: "status",
				header: ({ column }) => <DataTableColumnHeader column={column} label="Status" />,
				cell: ({ row }) => (
					<Badge variant="outline" className={getStatusBadgeClass(row.original.status)}>
						{getStatusLabel(row.original.status)}
					</Badge>
				),
				meta: {
					label: "Status",
					variant: "select",
					options: INTERNATIONAL_PURCHASE_STATUS_OPTIONS.map((option) => ({
						label: option.label,
						value: option.value,
					})),
				},
				enableColumnFilter: true,
			},
			{
				id: "supplierName",
				accessorKey: "supplierName",
				header: ({ column }) => <DataTableColumnHeader column={column} label="Supplier / Invoice" />,
				cell: ({ row }) => (
					<div className="min-w-[13rem] space-y-1">
						<p className="font-medium text-foreground">{row.original.supplierName}</p>
						<p className="text-xs text-muted-foreground">
							{row.original.invoiceNumber ? `Invoice ${row.original.invoiceNumber}` : "No invoice #"}
							{row.original.purchaseOrderNumber ? ` · PO ${row.original.purchaseOrderNumber}` : ""}
						</p>
					</div>
				),
				meta: { label: "Supplier", placeholder: "Search suppliers", variant: "text" },
				enableColumnFilter: true,
			},
			{
				id: "paymentStatus",
				accessorKey: "paymentStatus",
				header: ({ column }) => <DataTableColumnHeader column={column} label="Payment" />,
				cell: ({ row }) => (
					<Badge variant="outline" className={getPaymentStatusBadgeClass(row.original.paymentStatus)}>
						{getPaymentStatusLabel(row.original.paymentStatus)}
					</Badge>
				),
				meta: {
					label: "Payment",
					variant: "select",
					options: PAYMENT_STATUS_OPTIONS.map((option) => ({
						label: option.label,
						value: option.value,
					})),
				},
				enableColumnFilter: true,
			},
			{
				id: "checklist",
				accessorFn: (row) => getChecklistCompletion(row).completed,
				header: ({ column }) => <DataTableColumnHeader column={column} label="Import Docs" />,
				cell: ({ row }) => <ChecklistProgressCell purchase={row.original} />,
				meta: { label: "Import Docs", variant: "number", placeholder: "Min docs" },
				enableColumnFilter: true,
			},
			{
				id: "nextAction",
				accessorFn: (row) => getNextAction(row).label,
				header: ({ column }) => <DataTableColumnHeader column={column} label="Next Action" />,
				cell: ({ row }) => {
					const action = getNextAction(row.original);
					return (
						<div className="min-w-[13rem] space-y-1">
							<Badge variant="outline" className={getNextActionBadgeClass(action.tone)}>
								{action.label}
							</Badge>
							<p className="text-xs text-muted-foreground">{action.detail}</p>
						</div>
					);
				},
				meta: { label: "Next Action", placeholder: "Search next action", variant: "text" },
				enableColumnFilter: true,
			},
			{
				id: "invoiceAmountUsd",
				accessorKey: "invoiceAmountUsd",
				header: ({ column }) => <DataTableColumnHeader column={column} label="Amount" />,
				cell: ({ row }) => (
					<span className="tabular-nums">
						{typeof row.original.invoiceAmountUsd === "number"
							? formatCurrency(row.original.invoiceAmountUsd)
							: "-"}
					</span>
				),
				meta: { label: "Amount", variant: "number", placeholder: "Min amount" },
				enableColumnFilter: true,
			},
			{
				id: "routing",
				accessorFn: (row) => [row.vesselName, row.portOfLoading, row.destination].filter(Boolean).join(" "),
				header: ({ column }) => <DataTableColumnHeader column={column} label="Routing" />,
				cell: ({ row }) => (
					<div className="min-w-[12rem] space-y-1 text-sm">
						<p className="font-medium text-foreground">{row.original.vesselName || "-"}</p>
						<p className="text-xs text-muted-foreground">
							{[row.original.portOfLoading, row.original.destination].filter(Boolean).join(" → ") || "-"}
						</p>
					</div>
				),
				meta: { label: "Routing", placeholder: "Search routing", variant: "text" },
				enableColumnFilter: true,
			},
			{
				id: "sailing",
				accessorFn: (row) => row.estimatedSailingDate ?? "",
				header: ({ column }) => <DataTableColumnHeader column={column} label="Sailing" />,
				cell: ({ row }) => <span className="text-sm">{formatDateOnly(row.original.estimatedSailingDate)}</span>,
			},
			{
				id: "arrival",
				accessorFn: (row) => row.estimatedArrivalDate ?? "",
				header: ({ column }) => <DataTableColumnHeader column={column} label="Arrival" />,
				cell: ({ row }) => <span className="text-sm">{formatDateOnly(row.original.estimatedArrivalDate)}</span>,
			},
			{
				id: "documents",
				accessorFn: (row) => getDocumentLinks(row).length,
				header: ({ column }) => <DataTableColumnHeader column={column} label="Files" />,
				cell: ({ row }) => <InternationalPurchaseDocumentsCell purchase={row.original} />,
				meta: { label: "Files", variant: "number", placeholder: "Min files" },
				enableColumnFilter: true,
			},
			{
				id: "updatedAt",
				accessorKey: "updatedAt",
				header: ({ column }) => <DataTableColumnHeader column={column} label="Updated" />,
				cell: ({ row }) => <span className="text-sm text-muted-foreground">{formatTimestamp(row.original.updatedAt)}</span>,
			},
		];

		if (canEdit) {
			columns.push({
				id: "actions",
				header: () => <span className="sr-only">Actions</span>,
				cell: ({ row }) => (
					<Button variant="ghost" size="sm" onClick={() => onEdit(row.original)}>
						<Pencil className="mr-1.5 size-3.5" />
						Edit
					</Button>
				),
				enableSorting: false,
				enableColumnFilter: false,
			});
		}

		return columns;
	}, [canEdit, onEdit]);
}

export default function LogisticsInternationalPurchasesPage() {
	const { authUserId, userRole } = useAuthIdentity();
	const canEdit = hasPermission(userRole, "logistics:edit");
	const internationalPurchases = useQuery(
		api.internationalPurchases.listInternationalPurchases,
		authUserId ? { authUserId } : "skip",
	);
	const createInternationalPurchase = useMutation(api.internationalPurchases.createInternationalPurchase);
	const updateInternationalPurchase = useMutation(api.internationalPurchases.updateInternationalPurchase);
	const uploadInternationalPurchaseDocument = useAction(
		api.internationalPurchases.uploadInternationalPurchaseDocument,
	);
	const removeInternationalPurchaseDocument = useAction(
		api.internationalPurchases.removeInternationalPurchaseDocument,
	);

	const [activeTab, setActiveTab] = React.useState<InternationalPurchasesTab>("all");
	const suppliers = useQuery(
		api.suppliers.listSuppliers,
		authUserId ? { authUserId } : "skip",
	);

	const [activeFormTab, setActiveFormTab] = React.useState<InternationalPurchaseFormTab>("parties");
	const [dialogState, setDialogState] = React.useState<InternationalPurchaseDialogState | null>(null);
	const [form, setForm] = React.useState<InternationalPurchaseFormState>(buildEmptyFormState());
	const [documentDrafts, setDocumentDrafts] = React.useState<InternationalPurchaseDocumentDrafts>(
		createEmptyDocumentDrafts(),
	);
	const [isSaving, setIsSaving] = React.useState(false);

	React.useEffect(() => {
		if (!dialogState) return;
		setActiveFormTab("parties");
		setForm(dialogState.mode === "edit" ? buildFormState(dialogState.purchase) : buildCreateFormState());
		setDocumentDrafts(createEmptyDocumentDrafts());
	}, [dialogState]);

	const openCreateDialog = React.useCallback(() => {
		setDialogState({ mode: "create" });
	}, []);

	const openEditDialog = React.useCallback((purchase: InternationalPurchaseRecord) => {
		setDialogState({ mode: "edit", purchase });
	}, []);

	const columns = useColumns(openEditDialog, canEdit);

	const purchases = React.useMemo(() => {
		const records = (internationalPurchases ?? []) as InternationalPurchaseRecord[];
		return [...records].sort((left, right) => right.updatedAt - left.updatedAt);
	}, [internationalPurchases]);

	const tabCounts = React.useMemo(() => {
		const counts: Record<InternationalPurchasesTab, number> = {
			all: purchases.length,
			collecting_documents: 0,
			ready_for_payment: 0,
			paid: 0,
			in_transit: 0,
			received: 0,
			cancelled: 0,
		};
		for (const purchase of purchases) {
			counts[purchase.status] += 1;
		}
		return counts;
	}, [purchases]);

	const stats = React.useMemo(() => {
		const open = purchases.filter(
			(purchase) => !["received", "cancelled"].includes(purchase.status),
		).length;
		const waitingDocs = purchases.filter((purchase) => {
			const completion = getChecklistCompletion(purchase);
			return purchase.status === "collecting_documents" || completion.completed < completion.total;
		}).length;
		const readyToPay = purchases.filter((purchase) => purchase.paymentStatus === "ready_to_pay").length;
		const inTransit = purchases.filter((purchase) => purchase.status === "in_transit").length;
		return { open, waitingDocs, readyToPay, inTransit };
	}, [purchases]);

	const filteredPurchases = React.useMemo(() => {
		if (activeTab === "all") return purchases;
		return purchases.filter((purchase) => purchase.status === activeTab);
	}, [activeTab, purchases]);

	const setField = React.useCallback(
		<K extends keyof InternationalPurchaseFormState>(
			field: K,
			value: InternationalPurchaseFormState[K],
		) => {
			setForm((current) => ({ ...current, [field]: value }));
		},
		[],
	);

	const handleSupplierSelect = React.useCallback(
		(supplierId: string) => {
			if (!supplierId) {
				setField("supplierId", "");
				return;
			}

			const selected = (suppliers as SupplierRecord[] | undefined)?.find((s) => s._id === supplierId);
			if (!selected) {
				setField("supplierId", supplierId);
				return;
			}

			setForm((current) => ({
				...current,
				supplierId: selected._id,
				supplierName: selected.name,
				supplierAddress: selected.address ?? "",
				paymentTerms: selected.paymentTerms ?? current.paymentTerms,
				supplierPaymentInstructions: selected.paymentInstructions ?? current.supplierPaymentInstructions,
				manufacturerName: selected.name,
				manufacturerAddress: selected.address ?? "",
				manufacturerFdaRegistrationNumber: selected.fdaRegistrationNumber ?? "",
				sellerName: selected.name,
				sellerAddress: selected.address ?? "",
				sellerFdaRegistrationNumber: selected.fdaRegistrationNumber ?? "",
			}));
		},
		[suppliers, setField],
	);

	const setDocumentDraft = React.useCallback(
		(
			documentType: InternationalPurchaseDocumentType,
			updater: (
				current: InternationalPurchaseDocumentDraft,
			) => InternationalPurchaseDocumentDraft,
		) => {
			setDocumentDrafts((current) => ({
				...current,
				[documentType]: updater(current[documentType]),
			}));
		},
		[],
	);

	const handleDocumentFileSelection = React.useCallback(
		(documentType: InternationalPurchaseDocumentType, file: File) => {
			if (!isAllowedDocumentFile(file)) {
				toast.error(`${getDocumentLabel(documentType)} must be a PDF, XLSX, XLS, or CSV file.`);
				return;
			}

			if (file.size > DOCUMENT_MAX_BYTES) {
				toast.error(`${getDocumentLabel(documentType)} must be 5 MB or smaller.`);
				return;
			}

			setDocumentDraft(documentType, () => ({
				file,
				removeExisting: false,
			}));

			const checklistKey = DOCUMENT_TYPES.find((item) => item.key === documentType)?.checklistKey;
			if (checklistKey) {
				setField(checklistKey, true);
			}
		},
		[setDocumentDraft, setField],
	);

	const handleDialogOpenChange = React.useCallback((open: boolean) => {
		if (!open) {
			setDialogState(null);
			setDocumentDrafts(createEmptyDocumentDrafts());
			setIsSaving(false);
		}
	}, []);

	const persistDocuments = React.useCallback(
		async (purchase: InternationalPurchaseRecord) => {
			const errors: string[] = [];

			for (const documentType of DOCUMENT_TYPES) {
				const draft = documentDrafts[documentType.key];
				const existingDocument = getPurchaseDocument(purchase, documentType.key);

				if (draft.file) {
					try {
						const buffer = await draft.file.arrayBuffer();
						await uploadInternationalPurchaseDocument({
							purchaseId: purchase._id as any,
							documentType: documentType.key,
							fileData: buffer,
							contentType: draft.file.type || "application/octet-stream",
							filename: draft.file.name,
							fileSize: draft.file.size,
						});
					} catch (error) {
						errors.push(
							`${documentType.label}: ${error instanceof Error ? error.message : "Upload failed"}`,
						);
					}
					continue;
				}

				if (draft.removeExisting && existingDocument) {
					try {
						await removeInternationalPurchaseDocument({
							purchaseId: purchase._id as any,
							documentType: documentType.key,
						});
					} catch (error) {
						errors.push(
							`${documentType.label}: ${error instanceof Error ? error.message : "Remove failed"}`,
						);
					}
				}
			}

			return errors;
		},
		[documentDrafts, removeInternationalPurchaseDocument, uploadInternationalPurchaseDocument],
	);

	const handleSubmit = React.useCallback(async () => {
		if (!dialogState || isSaving || !canEdit) return;

		const supplierName = form.supplierName.trim() || form.manufacturerName.trim();
		if (!supplierName) {
			toast.error("Supplier name is required.");
			return;
		}

		let invoiceAmountUsd: number | null;
		let containerCount: number | null;
		try {
			invoiceAmountUsd = nullableAmount(form.invoiceAmountUsd);
			containerCount = nullableContainerCount(form.containerCount);
		} catch (error) {
			toast.error(error instanceof Error ? error.message : "Invalid number field.");
			return;
		}

		const payload = {
			supplierId: nullableText(form.supplierId),
			supplierName,
			supplierAddress: nullableText(form.supplierAddress),
			purchaseOrderNumber: nullableText(form.purchaseOrderNumber),
			sellerReference: nullableText(form.sellerReference),
			invoiceNumber: nullableText(form.invoiceNumber),
			invoiceDate: nullableText(form.invoiceDate),
			invoiceAmountUsd,
			invoiceLineItems: nullableText(form.invoiceLineItems),
			paymentTerms: nullableText(form.paymentTerms),
			supplierPaymentInstructions: nullableText(form.supplierPaymentInstructions),
			paymentDueDate: nullableText(form.paymentDueDate),
			paymentStatus: form.paymentStatus,
			masterBillOfLadingNumber: nullableText(form.masterBillOfLadingNumber),
			houseBillOfLadingNumber: nullableText(form.houseBillOfLadingNumber),
			quantityUnits: nullableText(form.quantityUnits),
			containerCount,
			vesselName: nullableText(form.vesselName),
			portOfLoading: nullableText(form.portOfLoading),
			destination: nullableText(form.destination),
			estimatedSailingDate: nullableText(form.estimatedSailingDate),
			estimatedArrivalDate: nullableText(form.estimatedArrivalDate),
			countryOfOrigin: nullableText(form.countryOfOrigin),
			commodityDescription: nullableText(form.commodityDescription),
			harmonizedTariffSchedule: nullableText(form.harmonizedTariffSchedule),
			manufacturerName: nullableText(form.manufacturerName),
			manufacturerAddress: nullableText(form.manufacturerAddress),
			manufacturerFdaRegistrationNumber: nullableText(form.manufacturerFdaRegistrationNumber),
			sellerName: nullableText(form.sellerName),
			sellerAddress: nullableText(form.sellerAddress),
			sellerFdaRegistrationNumber: nullableText(form.sellerFdaRegistrationNumber),
			buyerName: nullableText(form.buyerName),
			buyerAddress: nullableText(form.buyerAddress),
			shipToName: nullableText(form.shipToName),
			shipToAddress: nullableText(form.shipToAddress),
			importerOfRecordName: nullableText(form.importerOfRecordName),
			importerOfRecordAddress: nullableText(form.importerOfRecordAddress),
			importerOfRecordFederalId: nullableText(form.importerOfRecordFederalId),
			consigneeName: nullableText(form.consigneeName),
			consigneeAddress: nullableText(form.consigneeAddress),
			consigneeFederalId: nullableText(form.consigneeFederalId),
			containerStuffingLocationName: nullableText(form.containerStuffingLocationName),
			containerStuffingLocationAddress: nullableText(form.containerStuffingLocationAddress),
			containerStuffingLocationFdaRegistrationNumber: nullableText(form.containerStuffingLocationFdaRegistrationNumber),
			containerStufferName: nullableText(form.containerStufferName),
			containerStufferAddress: nullableText(form.containerStufferAddress),
			containerStufferFdaRegistrationNumber: nullableText(form.containerStufferFdaRegistrationNumber),
			commercialInvoiceReceived: form.commercialInvoiceReceived,
			isfFormComplete: form.isfFormComplete,
			packingListReceived: form.packingListReceived,
			billOfLadingReceived: form.billOfLadingReceived,
			arrivalNoticeReceived: form.arrivalNoticeReceived,
			customsEntryReceived: form.customsEntryReceived,
			freightInvoiceReceived: form.freightInvoiceReceived,
			notes: nullableText(form.notes),
			status: form.status,
		};

		setIsSaving(true);
		try {
			let savedPurchase: InternationalPurchaseRecord;
			if (dialogState.mode === "edit") {
				savedPurchase = (await updateInternationalPurchase({
					purchaseId: dialogState.purchase._id as any,
					...payload,
				})) as InternationalPurchaseRecord;
			} else {
				savedPurchase = (await createInternationalPurchase(payload)) as InternationalPurchaseRecord;
			}

			const documentErrors = await persistDocuments(savedPurchase);
			if (documentErrors.length > 0) {
				toast.error(
					`International purchase saved, but some documents failed: ${documentErrors.join("; ")}`,
				);
			} else {
				toast.success(
					dialogState.mode === "edit"
						? "International purchase updated."
						: "International purchase created.",
				);
			}

			setDialogState(null);
			setForm(buildEmptyFormState());
			setDocumentDrafts(createEmptyDocumentDrafts());
		} catch (error) {
			toast.error(error instanceof Error ? error.message : "Failed to save international purchase.");
		} finally {
			setIsSaving(false);
		}
	}, [
		canEdit,
		createInternationalPurchase,
		dialogState,
		form,
		isSaving,
		persistDocuments,
		updateInternationalPurchase,
	]);

	return (
		<div className="space-y-6">
			<div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
				<div>
					<h1 className="text-3xl font-bold tracking-tight">International Purchases</h1>
					<p className="mt-1 max-w-3xl text-muted-foreground">
						Track importer security filing fields, supplier invoices, bill-of-lading details,
						and the document checklist before Anthem releases payment.
					</p>
				</div>
				{canEdit ? <Button onClick={openCreateDialog}>Create Purchase</Button> : null}
			</div>

			<section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
				<InternalMetricCard title="Open Purchases" value={String(stats.open)} hint="Not received or cancelled" icon={Ship} />
				<InternalMetricCard title="Waiting on Docs" value={String(stats.waitingDocs)} hint="Import checklist not complete" icon={AlertTriangle} />
				<InternalMetricCard title="Ready to Pay" value={String(stats.readyToPay)} hint="Invoice can be paid" icon={DollarSign} />
				<InternalMetricCard title="In Transit" value={String(stats.inTransit)} hint="Paid or moving toward port / warehouse" icon={CheckCircle2} />
			</section>

			<Card>
				<CardHeader>
					<CardTitle>Import Documentation Queue</CardTitle>
					<CardDescription>
						Checklist covers commercial invoice, ISF form, packing list, B/L, arrival notice,
						customs entry, and freight invoice so JV can work the import-doc blockers in one place.
					</CardDescription>
				</CardHeader>
				<CardContent>
					<Tabs
						value={activeTab}
						onValueChange={(value) => setActiveTab(value as InternationalPurchasesTab)}
						className="mb-4"
					>
						<TabsList className="flex h-auto flex-wrap justify-start gap-1">
							<TabsTrigger value="all">
								All
								<Badge variant="secondary" className="ml-2">
									{tabCounts.all}
								</Badge>
							</TabsTrigger>
							{INTERNATIONAL_PURCHASE_STATUS_OPTIONS.map((status) => (
								<TabsTrigger key={status.value} value={status.value}>
									{status.label}
									<Badge variant="secondary" className="ml-2">
										{tabCounts[status.value]}
									</Badge>
								</TabsTrigger>
							))}
						</TabsList>
					</Tabs>

					{internationalPurchases === undefined ? (
						<DataTableSkeleton columnCount={columns.length} rowCount={10} withPagination />
					) : (
						<InternalEntityDataTable
							data={filteredPurchases}
							columns={columns}
							enableSelection={false}
						/>
					)}
				</CardContent>
			</Card>

			<Dialog open={dialogState !== null} onOpenChange={handleDialogOpenChange}>
				<DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl">
					<div className="shrink-0 space-y-4 border-b px-6 pt-6 pb-4">
						<DialogHeader>
							<DialogTitle className="text-balance">
								{dialogState?.mode === "edit"
									? "Edit International Purchase"
									: "Create International Purchase"}
							</DialogTitle>
							<DialogDescription className="text-pretty">
								{dialogState?.mode === "edit"
									? "Update invoice details, ISF fields, and import documents for this purchase."
									: "Start on Parties: link the supplier, then add invoice and routing details."}
							</DialogDescription>
						</DialogHeader>
						<CompactReadinessBar purchase={form} />
					</div>

					<Tabs
						value={activeFormTab}
						onValueChange={(value) => setActiveFormTab(value as InternationalPurchaseFormTab)}
						className="flex min-h-0 flex-1 flex-col"
					>
						<div className="shrink-0 border-b px-6 py-3">
							<TabsList variant="line" className="grid w-full grid-cols-2 sm:grid-cols-5">
								{INTERNATIONAL_PURCHASE_FORM_TABS.map((tab) => {
									const TabIcon = tab.icon;
									return (
										<TabsTrigger
											key={tab.value}
											value={tab.value}
											className="h-9 gap-1.5 px-2 text-xs sm:text-sm"
										>
											<TabIcon className="size-3.5 shrink-0" />
											<span className="truncate">{tab.label}</span>
										</TabsTrigger>
									);
								})}
							</TabsList>
						</div>

						<div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
							<TabsContent value="invoice" className="mt-0 space-y-4">
								<FormSection
									title="Purchase & Payment"
									description="Use the supplier invoice as the source of truth for payment status."
									icon={Receipt}
								>
									<div className="space-y-8">
										<FormSubsection title="Invoice details">
											<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
												<div className="grid gap-2">
													<Label htmlFor="international-purchase-po">PO #</Label>
													<Input
														id="international-purchase-po"
														value={form.purchaseOrderNumber}
														onChange={(event) => setField("purchaseOrderNumber", event.target.value)}
														placeholder="Purchase order"
													/>
												</div>
												<div className="grid gap-2">
													<Label htmlFor="international-purchase-seller-reference">Seller ref</Label>
													<Input
														id="international-purchase-seller-reference"
														value={form.sellerReference}
														onChange={(event) => setField("sellerReference", event.target.value)}
														placeholder="Supplier reference"
													/>
												</div>
												<div className="grid gap-2">
													<Label htmlFor="international-purchase-invoice">Invoice #</Label>
													<Input
														id="international-purchase-invoice"
														value={form.invoiceNumber}
														onChange={(event) => setField("invoiceNumber", event.target.value)}
														placeholder="Invoice number"
													/>
												</div>
												<div className="grid gap-2">
													<Label htmlFor="international-purchase-invoice-date">Invoice date</Label>
													<DatePicker
														id="international-purchase-invoice-date"
														value={form.invoiceDate}
														onChange={(value) => setField("invoiceDate", value)}
														placeholder="Pick invoice date"
													/>
												</div>
												<div className="grid gap-2">
													<Label htmlFor="international-purchase-invoice-amount">Invoice amount USD</Label>
													<Input
														id="international-purchase-invoice-amount"
														inputMode="decimal"
														className="tabular-nums"
														value={form.invoiceAmountUsd}
														onChange={(event) => setField("invoiceAmountUsd", event.target.value)}
														placeholder="186998.30"
													/>
												</div>
												<div className="grid gap-2 sm:col-span-2 lg:col-span-3">
													<Label htmlFor="international-purchase-line-items">Invoice line items</Label>
													<Textarea
														id="international-purchase-line-items"
														value={form.invoiceLineItems}
														onChange={(event) => setField("invoiceLineItems", event.target.value)}
														placeholder="Article numbers, descriptions, quantities, unit prices, and amounts"
														rows={3}
													/>
												</div>
											</div>
										</FormSubsection>

										<FormSubsection title="Payment">
											<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
												<div className="grid gap-2 sm:col-span-2">
													<Label htmlFor="international-purchase-payment-terms">Payment terms</Label>
													<Input
														id="international-purchase-payment-terms"
														value={form.paymentTerms}
														onChange={(event) => setField("paymentTerms", event.target.value)}
														placeholder="Payment timing from invoice / B/L release"
													/>
												</div>
												<div className="grid gap-2">
													<Label htmlFor="international-purchase-payment-due-date">Payment due date</Label>
													<DatePicker
														id="international-purchase-payment-due-date"
														value={form.paymentDueDate}
														onChange={(value) => setField("paymentDueDate", value)}
														placeholder="Pick due date"
													/>
												</div>
												<div className="grid gap-2 sm:col-span-2 lg:col-span-3">
													<Label htmlFor="international-purchase-payment-instructions">
														Supplier payment instructions
													</Label>
													<Textarea
														id="international-purchase-payment-instructions"
														value={form.supplierPaymentInstructions}
														onChange={(event) =>
															setField("supplierPaymentInstructions", event.target.value)
														}
														placeholder="Bank, IBAN, SWIFT/BIC, tax notes, or remittance instructions"
														rows={3}
													/>
												</div>
												<div className="grid gap-2">
													<Label htmlFor="international-purchase-payment-status">Payment status</Label>
													<Select
														value={form.paymentStatus}
														onValueChange={(value) =>
															setField("paymentStatus", value as PaymentStatus)
														}
													>
														<SelectTrigger id="international-purchase-payment-status">
															<SelectValue placeholder="Select payment status" />
														</SelectTrigger>
														<SelectContent>
															{PAYMENT_STATUS_OPTIONS.map((option) => (
																<SelectItem key={option.value} value={option.value}>
																	{option.label}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												</div>
												<div className="grid gap-2">
													<Label htmlFor="international-purchase-status">Workflow status</Label>
													<Select
														value={form.status}
														onValueChange={(value) =>
															setField("status", value as InternationalPurchaseStatus)
														}
													>
														<SelectTrigger id="international-purchase-status">
															<SelectValue placeholder="Select status" />
														</SelectTrigger>
														<SelectContent>
															{INTERNATIONAL_PURCHASE_STATUS_OPTIONS.map((option) => (
																<SelectItem key={option.value} value={option.value}>
																	{option.label}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												</div>
											</div>
										</FormSubsection>
									</div>
								</FormSection>
							</TabsContent>

							<TabsContent value="routing" className="mt-0 space-y-4">
								<FormSection
									title="ISF & Routing Fields"
									description="B/L, quantity, containers, vessel, loading port, destination, origin, commodity, and HTS."
									icon={MapPin}
								>
									<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
										<div className="grid gap-2">
											<Label htmlFor="international-purchase-master-bl">Master B/L #</Label>
											<Input
												id="international-purchase-master-bl"
												value={form.masterBillOfLadingNumber}
												onChange={(event) =>
													setField("masterBillOfLadingNumber", event.target.value)
												}
												placeholder="Master bill of lading"
											/>
										</div>
										<div className="grid gap-2">
											<Label htmlFor="international-purchase-house-bl">House B/L #</Label>
											<Input
												id="international-purchase-house-bl"
												value={form.houseBillOfLadingNumber}
												onChange={(event) =>
													setField("houseBillOfLadingNumber", event.target.value)
												}
												placeholder="House bill of lading"
											/>
										</div>
										<div className="grid gap-2">
											<Label htmlFor="international-purchase-quantity">Quantity / Units</Label>
											<Input
												id="international-purchase-quantity"
												value={form.quantityUnits}
												onChange={(event) => setField("quantityUnits", event.target.value)}
												placeholder="281 bags / 8120 kg"
											/>
										</div>
										<div className="grid gap-2">
											<Label htmlFor="international-purchase-containers">Containers</Label>
											<Input
												id="international-purchase-containers"
												inputMode="numeric"
												className="tabular-nums"
												value={form.containerCount}
												onChange={(event) => setField("containerCount", event.target.value)}
												placeholder="1"
											/>
										</div>
										<div className="grid gap-2">
											<Label htmlFor="international-purchase-vessel">Vessel / Voyage</Label>
											<Input
												id="international-purchase-vessel"
												value={form.vesselName}
												onChange={(event) => setField("vesselName", event.target.value)}
												placeholder="Vessel and voyage"
											/>
										</div>
										<div className="grid gap-2">
											<Label htmlFor="international-purchase-port-loading">Port of Loading</Label>
											<Input
												id="international-purchase-port-loading"
												value={form.portOfLoading}
												onChange={(event) => setField("portOfLoading", event.target.value)}
												placeholder="Port of loading"
											/>
										</div>
										<div className="grid gap-2">
											<Label htmlFor="international-purchase-destination">Destination</Label>
											<Input
												id="international-purchase-destination"
												value={form.destination}
												onChange={(event) => setField("destination", event.target.value)}
												placeholder="Destination city / country"
											/>
										</div>
										<div className="grid gap-2">
											<Label htmlFor="international-purchase-sailing-date">Estimated Sailing</Label>
											<DatePicker
												id="international-purchase-sailing-date"
												value={form.estimatedSailingDate}
												onChange={(value) => setField("estimatedSailingDate", value)}
												placeholder="Pick sailing date"
											/>
										</div>
										<div className="grid gap-2">
											<Label htmlFor="international-purchase-arrival-date">Estimated Arrival</Label>
											<DatePicker
												id="international-purchase-arrival-date"
												value={form.estimatedArrivalDate}
												onChange={(value) => setField("estimatedArrivalDate", value)}
												placeholder="Pick arrival date"
											/>
										</div>
										<div className="grid gap-2">
											<Label htmlFor="international-purchase-origin">Country of Origin</Label>
											<Input
												id="international-purchase-origin"
												value={form.countryOfOrigin}
												onChange={(event) => setField("countryOfOrigin", event.target.value)}
												placeholder="Country of origin"
											/>
										</div>
										<div className="grid gap-2">
											<Label htmlFor="international-purchase-commodity">Commodity</Label>
											<Input
												id="international-purchase-commodity"
												value={form.commodityDescription}
												onChange={(event) => setField("commodityDescription", event.target.value)}
												placeholder="Commodity description"
											/>
										</div>
										<div className="grid gap-2">
											<Label htmlFor="international-purchase-hts">HTS</Label>
											<Input
												id="international-purchase-hts"
												value={form.harmonizedTariffSchedule}
												onChange={(event) =>
													setField("harmonizedTariffSchedule", event.target.value)
												}
												placeholder="Harmonized tariff schedule"
											/>
										</div>
									</div>
								</FormSection>
							</TabsContent>

							<TabsContent value="parties" className="mt-0 space-y-4">
								<FormSection
									title="ISF Parties & Identification"
									description="Work through requirements 1–8 in order. Seller-side parties first, then importer-side."
									icon={Users}
								>
									<IsfPartiesPanel
										form={form}
										suppliers={suppliers as SupplierRecord[] | undefined}
										setForm={setForm}
										onSupplierSelect={handleSupplierSelect}
									/>
								</FormSection>
							</TabsContent>

							<TabsContent value="documents" className="mt-0 space-y-4">
								<FormSection
									title="Import Docs Checklist"
									description="Check items off as docs arrive. Attachments below can be added now or later."
									icon={ClipboardCheck}
								>
									<div className="grid gap-2 sm:grid-cols-2">
										{IMPORT_CHECKLIST_ITEMS.map((item) => (
											<label
												key={item.key}
												className="flex cursor-pointer items-center gap-3 rounded-lg bg-background px-4 py-3 ring-1 ring-border/60 transition-colors hover:bg-muted/40 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring"
											>
												<Checkbox
													checked={form[item.checklistKey]}
													onCheckedChange={(checked) =>
														setField(item.checklistKey, checked === true)
													}
												/>
												<span className="text-sm font-medium text-foreground">{item.label}</span>
											</label>
										))}
									</div>
								</FormSection>

								<FormSection
									title="Attachments"
									description="Upload invoice PDF, ISF spreadsheet, B/L, and other import docs. Files upload to R2 when saved."
									icon={Upload}
								>
									<div className="grid gap-3">
										{DOCUMENT_TYPES.map((documentType) => (
											<InternationalPurchaseDocumentUploadField
												key={documentType.key}
												documentType={documentType.key}
												existingDocument={
													dialogState?.mode === "edit"
														? getPurchaseDocument(dialogState.purchase, documentType.key)
														: undefined
												}
												draft={documentDrafts[documentType.key]}
												onSelectFile={(file) =>
													handleDocumentFileSelection(documentType.key, file)
												}
												onClearSelectedFile={() =>
													setDocumentDraft(documentType.key, (current) => ({
														...current,
														file: null,
													}))
												}
												onMarkRemoveExisting={() =>
													setDocumentDraft(documentType.key, () => ({
														file: null,
														removeExisting: true,
													}))
												}
												onUndoRemoveExisting={() =>
													setDocumentDraft(documentType.key, () => ({
														file: null,
														removeExisting: false,
													}))
												}
											/>
										))}
									</div>
								</FormSection>
							</TabsContent>

							<TabsContent value="notes" className="mt-0 space-y-4">
								<FormSection
									title="Internal Notes"
									description="Missing docs, broker notes, B/L release status, payment blockers, or follow-up context."
									icon={StickyNote}
								>
									<Textarea
										id="international-purchase-notes"
										value={form.notes}
										onChange={(event) => setField("notes", event.target.value)}
										placeholder="Add context for the team working this import..."
										rows={8}
										className="min-h-[180px] resize-y"
									/>
								</FormSection>

								{dialogState?.mode === "edit" ? (
									<div className="grid gap-2 rounded-xl bg-muted/30 px-4 py-3 text-sm text-muted-foreground ring-1 ring-border/50 sm:grid-cols-2">
										<div>
											<span className="font-medium text-foreground">Created:</span>{" "}
											{formatTimestamp(dialogState.purchase.createdAt)}
										</div>
										<div>
											<span className="font-medium text-foreground">Updated:</span>{" "}
											{formatTimestamp(dialogState.purchase.updatedAt)}
										</div>
									</div>
								) : null}
							</TabsContent>
						</div>
					</Tabs>

					<DialogFooter className="shrink-0 border-t px-6 py-4 sm:justify-between">
						<p className="hidden text-xs text-muted-foreground sm:block">
							{activeFormTab === "parties" &&
							!form.supplierName.trim() &&
							!form.manufacturerName.trim()
								? "Enter supplier details in party #1 to save."
								: "Use tabs to move between sections without losing data."}
						</p>
						<div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row">
							<Button
								variant="outline"
								className="transition-transform active:scale-[0.96]"
								onClick={() => handleDialogOpenChange(false)}
								disabled={isSaving}
							>
								Cancel
							</Button>
							<Button
								className="transition-transform active:scale-[0.96]"
								onClick={() => void handleSubmit()}
								disabled={isSaving || !canEdit}
							>
								{isSaving ? (
									<>
										<Loader2 className="mr-2 size-4 animate-spin" />
										Saving
									</>
								) : dialogState?.mode === "edit" ? (
									"Save Changes"
								) : (
									"Create Purchase"
								)}
							</Button>
						</div>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
